import React, { useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { createUniversalRegCodesBulk } from '../../services/universalRegCodesService';
import BulkCreateResults from './BulkCreateResults';

const ManualMassCreate = () => {
  const { t } = useTranslation();

  const [rawEmails, setRawEmails] = useState('');
  const [error, setError] = useState(null);
  const [apiResult, setApiResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ new: send emails toggle
  const [sendEmails, setSendEmails] = useState(false);

  const parsedEmails = useMemo(() => {
    const list = rawEmails
      .split(/[\n,;]+/g)
      .map((s) => s.trim())
      .filter(Boolean);

    return Array.from(new Set(list));
  }, [rawEmails]);

  const handlePrepare = async () => {
    setError(null);
    setApiResult(null);

    const invalid = parsedEmails.filter((e) => !/^\S+@\S+\.\S+$/.test(e));
    if (invalid.length) {
      setError(
        t('massCreateCredentials.manual.invalidEmails', 'Invalid emails') +
          `: ${invalid.slice(0, 5).join(', ')}${invalid.length > 5 ? '…' : ''}`
      );
      return;
    }

    const items = parsedEmails.map((email) => ({
      email,
      source: 'manual',
      payload: {},
      // Optional per-row override if you ever want:
      // send_email: sendEmails,
    }));

    setIsSubmitting(true);
    const res = await createUniversalRegCodesBulk({
      items,
      default_send_email: sendEmails, // ✅ checkbox
    });
    setIsSubmitting(false);

    if (!res.success) {
      setError(res.error || 'Request failed');
      return;
    }

    setApiResult(res.data);
  };

  return (
    <Paper variant="outlined" sx={{ padding: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('massCreateCredentials.manual.title', 'Manual input')}
      </Typography>

      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}

        {/* ✅ Send email toggle */}
        <FormControlLabel
          control={
            <Checkbox
              checked={sendEmails}
              onChange={(e) => setSendEmails(e.target.checked)}
              disabled={isSubmitting}
            />
          }
          label={t('massCreateCredentials.sendEmails', 'Send registration emails automatically')}
        />

        {sendEmails && (
          <Alert severity="info">
            {t(
              'massCreateCredentials.sendEmailsInfo',
              'Emails will be sent to each address after codes are created.'
            )}
          </Alert>
        )}

        <TextField
          label={t(
            'massCreateCredentials.manual.emailsLabel',
            'Emails (one per line, or separated by comma/semicolon)'
          )}
          value={rawEmails}
          onChange={(e) => setRawEmails(e.target.value)}
          multiline
          minRows={6}
          fullWidth
        />

        <Alert severity="info">
          {t('massCreateCredentials.manual.preview', 'Parsed emails')}: {parsedEmails.length}
        </Alert>

        <Button
          variant="contained"
          onClick={handlePrepare}
          disabled={!parsedEmails.length || isSubmitting}
        >
          {isSubmitting
            ? t('common.working', 'Working…')
            : sendEmails
              ? t('massCreateCredentials.manual.prepareAndSend', 'Prepare & send emails')
              : t('massCreateCredentials.manual.prepare', 'Prepare batch')}
        </Button>

        {/* ✅ Results tables */}
        <BulkCreateResults
          result={apiResult}
          title={t('massCreateCredentials.manual.results', 'Manual batch results')}
        />
      </Stack>
    </Paper>
  );
};

export default ManualMassCreate;
